'use client'

import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { IValidationError, sendForm } from '~/services/form'
import { IFormData } from '~/types/form'

import CMSButton from '../button/cms'
import RichText from '../rich-text'
import InputErrorMessage from './error-message'
import FormField from './field'

interface IProperties {
  isHidden?: boolean
  formId: number
  formData: IFormData
  formPrefix?: string
  className?: string
}

const Form: React.FC<IProperties> = ({
  formData,
  formId,
  isHidden = false,
  formPrefix = '',
  className,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm()
  const [isSending, setSending] = useState(false)
  const [isSended, setSended] = useState(false)

  const formName = useMemo(
    () =>
      `${formPrefix}${formData.name.replaceAll(' ', '-').toLocaleLowerCase()}`,
    [formData.name, formPrefix]
  )

  const onSubmitForm = useCallback(
    async (data: Record<string, string>) => {
      if (isSending || isSended) return

      setSending(true)
      clearErrors()

      try {
        await sendForm(formId, data)
        setSended(true)
      } catch (fetchError) {
        const errorData = fetchError?.data?.error
        const validations = errorData?.details
        if (validations && Array.isArray(validations)) {
          for (const validation of validations as IValidationError[]) {
            setError(
              validation.target,
              {
                type: 'validate',
                message: validation.message,
              },
              {
                shouldFocus: true,
              }
            )
          }
        } else {
          setError('root', {
            type: 'manual',
            message: errorData?.message || fetchError.message,
          })
        }
      } finally {
        setSending(false)
      }
    },
    [clearErrors, formId, isSended, isSending, setError]
  )

  return (
    <div className={classNames('relative', className)}>
      <div
        className={classNames(
          'absolute px-[inherit] flex items-center left-0 top-1/2 -translate-y-1/2 w-full transition-[opacity,transform] duration-500 ease-out',
          {
            'opacity-0 pointer-events-none -translate-x-10': !isSended,
          }
        )}
      >
        <RichText
          html={formData.tankyouMessage}
          className="text-current [&>*]:text-current prose-h2:text-4xl prose-h2:leading-[0.9]"
        />
      </div>
      <form
        onSubmit={handleSubmit(onSubmitForm)}
        className={classNames(
          'flex flex-col items-start gap-5 md:gap-6 transition-[opacity,transform] duration-500 ease-out',
          {
            'opacity-0 translate-x-10 pointer-events-none': isSended,
          }
        )}
      >
        {formData.formFields.map((field) => (
          <FormField
            key={`${formName}-${field.id}`}
            formName={formName}
            fieldData={field}
            register={register}
            error={errors[field.name]?.message as string}
            tabIndex={isHidden ? -1 : 0}
            optionalText="Opcional"
          />
        ))}
        {errors.root && <InputErrorMessage message={errors.root.message} />}
        <CMSButton
          component="button"
          type="submit"
          disabled={isSending || isSended}
          tabIndex={isHidden ? -1 : 0}
          attributes={formData.sendButton}
          className={classNames(
            'relative transition-all w-full sm:w-auto mt-2 md:mt-0',
            {
              'pr-12': isSending,
            }
          )}
        >
          <div
            className={classNames(
              'flex items-center absolute top-0 right-5 h-full transition-opacity ',
              {
                'opacity-0': !isSending,
              }
            )}
          >
            <div className="w-4 h-4 animate-spin border-solid border-[transparent] border-t-current rounded-full border-2" />
          </div>
        </CMSButton>
      </form>
    </div>
  )
}

export default Form
